import { carrierMediumLabel, type CarrierMedium } from "kontrolEdge/helpers/carrierAccess";

/** A link of a CPE's router, as the detail serves it. */
interface CpeLink {
  kind: "backbone-uplink" | "ha-interconnect";
  capacity: string | null;
  destination: string | null;
  carrierCode: string | null;
  carrierName: string | null;
  portAllocationId: number;
}

/** A carrier leg of the CPE, as the detail serves it. */
interface CpeLeg<Status> {
  id: number;
  portAllocationId?: number | null;
  carrierCode: string;
  name: string;
  capacity: string | null;
  medium?: CarrierMedium | null;
  status?: Status;
  statusMessage?: string | null;
  carrierAccess?: { id: number; serviceId: string } | null;
}

/** One row of the CPE's links table (CTX-801, SD-WAN style). */
export interface CpeLinkRow<Status> {
  key: string;
  label: string;
  speed: string | null;
  carrier: string | null;
  /** "Fiber Optic" / "Satellite" / "Not set"; null on a link with no carrier. */
  medium: string | null;
  /** The carrier's status; undefined on a link with no carrier. */
  status?: Status;
  statusMessage: string | null;
  carrierAccess: { id: number; serviceId: string } | null;
}

/**
 * The CPE's links with each carrier's status in the same row, as SD-WAN
 * shows its branches: an uplink takes the leg on its interface for its
 * carrier; the HA interconnect has no carrier; a leg on no listed link (a
 * last mile, a manual leg) gets a row of its own, so no carrier is left out.
 */
export function cpeLinkRows<Status>(
  links: CpeLink[],
  legs: CpeLeg<Status>[]
): CpeLinkRow<Status>[] {
  const used = new Set<number>();
  const legFor = (link: CpeLink) => {
    const onInterface = legs.filter(
      (leg) => !used.has(leg.id) && leg.portAllocationId === link.portAllocationId
    );
    return (
      onInterface.find((leg) => leg.carrierCode === link.carrierCode) ??
      onInterface[0]
    );
  };

  const fromLeg = (leg: CpeLeg<Status>) => ({
    carrier: leg.name,
    medium: carrierMediumLabel(leg.medium),
    status: leg.status,
    statusMessage: leg.statusMessage ?? null,
    carrierAccess: leg.carrierAccess ?? null,
  });

  const rows: CpeLinkRow<Status>[] = [];
  const uplinks = links.filter((link) => link.kind === "backbone-uplink");
  const interconnects = links.filter((link) => link.kind === "ha-interconnect");

  for (const link of uplinks) {
    const leg = legFor(link);
    if (leg) used.add(leg.id);
    rows.push({
      key: `link-${link.portAllocationId}`,
      label: `Uplink${link.destination ? ` — ${link.destination}` : ""}`,
      speed: link.capacity,
      ...(leg
        ? fromLeg(leg)
        : {
            carrier: link.carrierName,
            medium: null,
            statusMessage: null,
            carrierAccess: null,
          }),
    });
  }

  for (const leg of legs.filter((candidate) => !used.has(candidate.id))) {
    rows.push({
      key: `leg-${leg.id}`,
      label: "Carrier access",
      speed: leg.capacity,
      ...fromLeg(leg),
    });
  }

  for (const link of interconnects) {
    rows.push({
      key: `link-${link.portAllocationId}`,
      label: "HA interconnect",
      speed: link.capacity,
      carrier: null,
      medium: null,
      statusMessage: null,
      carrierAccess: null,
    });
  }

  return rows;
}
